import {Injectable} from '@angular/core';
import {JwtHelperService} from '@auth0/angular-jwt';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {BehaviorSubject, catchError, filter, map, Observable, take, throwError} from 'rxjs';
import {environment} from '../../environments/environment';
import {CustomHttpResponse} from '../model/custom-http-response';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private token: string | null | undefined = null;
  private refreshToken: string | null = null;
  private jwtHelper: JwtHelperService;
  private loggedInUsername: string | null = null;
  host = environment.apiUrl;
  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  constructor(private http: HttpClient, jwtHelper: JwtHelperService) {
    this.jwtHelper = jwtHelper;

  }

  public login(email: string, password: string): Observable<HttpResponse<CustomHttpResponse>> {
    const body = {email, password};

    return this.http
      .post<CustomHttpResponse>(
        `${this.host}/v1/staff/auth/login`, body, {observe: 'response'}
      );

  }

  public saveToken(token: string | null | undefined): void {
    this.token = token ?? null;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  public saveRefreshToken(token: string | null | undefined): void {
    this.refreshToken = token ?? null;
    if (token) {
      localStorage.setItem('refreshToken', token);
    } else {
      localStorage.removeItem('refreshToken');
    }
  }

  public getToken(): string | null {
    return localStorage.getItem('token');
  }

  public getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  private isTokenValid(): boolean {
    if (this.token != null && this.token !== '') {
      const {sub} = this.jwtHelper.decodeToken(this.token);
      if (sub != null && sub !== '' && !this.jwtHelper.isTokenExpired(this.token)) {
        this.loggedInUsername = sub;
        return true;
      }
    }
    return false;
  }

  public loadToken(): void {
    this.token = localStorage.getItem('token');
    this.refreshToken = localStorage.getItem('refreshToken');
  }

  public clearSession(): void {
    this.token = null;
    this.refreshToken = null;
    this.loggedInUsername = null;
    this.isRefreshing = false;
    this.refreshSubject.next(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');

  }

  public isLogin(): boolean {
    this.loadToken();
    if (this.isTokenValid()) {
      return true;
    } else {
      this.token = null;
      localStorage.removeItem('token');
      return false;
    }
  }

  public doRefreshToken(): Observable<any> {
    return this.http.post<any>(
      `${this.host}/v1/staff/auth/token/refresh`,
      {refreshToken: this.refreshToken},
      {observe: 'response'}
    );
  }

  public handleTokenRefresh(): Observable<string | null> {
    if (this.isRefreshing) {
      return this.refreshSubject.pipe(
        filter(token => token !== null),
        take(1)
      );
    }

    this.isRefreshing = true;
    this.refreshSubject.next(null);

    return this.doRefreshToken().pipe(
      map((response: HttpResponse<any>) => {
        const newAccess = response.body?.data?.access ?? null;
        const newRefresh = response.body?.data?.refresh ?? null;
        if (newAccess) this.saveToken(newAccess);
        if (newRefresh) this.saveRefreshToken(newRefresh);

        this.isRefreshing = false;
        this.refreshSubject.next(newAccess);
        return newAccess;
      }),
      catchError(err => {
        this.isRefreshing = false;
        this.refreshSubject.next(null);
        this.clearSession();
        return throwError(() => err);
      })
    );
  }

  logout(): Observable<HttpResponse<CustomHttpResponse>> {
    return this.http
      .post<CustomHttpResponse>(
        `${this.host}/v1/staff/auth/logout`, '', {observe: 'response'}
      );
  }
  resetPassword(resetURI: string, email: string): Observable<HttpResponse<CustomHttpResponse>> {
    const params = new HttpParams().set('resetURI', resetURI);
    return this.http.post<CustomHttpResponse>(`${this.host}/v1/staff/auth/password/rest`, {email}, {
      observe: 'response',
      params
    })
  }
  updatePassword(body: any): Observable<HttpResponse<CustomHttpResponse>> {
    return this.http.patch<CustomHttpResponse>(`${this.host}/v1/staff/auth/password/rest`, body, {observe: 'response'})
  }
}
